// src/pages/Settings/company/whatsapp/index.js
//
// Change #100326 - Settings > WhatsApp (id 54). Everything a company sets up once for WhatsApp:
// the Twilio numbers, approved templates, quick replies, automatic notifications and the
// opt-out register. Sub-tabs deep-link as /settings/1/54/<slug>; the inbox links to
// /settings/1/54/channels when no number is set up.
//
// Gated on the CRM_WHATSAPP module so a company without it sees the standard "module not
// active" page instead of screens whose API calls would all be refused.
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import "../../settings.styles.css";
import "../../../../Components/WhatsApp/whatsapp.scss";
import "./whatsappSettings.scss";
import SettingsPanel from "../../components/SettingsPanel";
import ModuleGate from "../../../../Components/Common/ModuleGate";
import { WHATSAPP_MODULE_CODE } from "../../../../Components/WhatsApp/whatsappUtils";
import { errorText, listChannels, ok } from "../../../../Components/WhatsApp/whatsappApi";
import ChannelsTab from "./ChannelsTab";
import TemplatesTab from "./TemplatesTab";
import QuickRepliesTab from "./QuickRepliesTab";
import NotificationsTab from "./NotificationsTab";
import ConsentTab from "./ConsentTab";

const TABS = [
  { value: "channels", slug: "channels", label: "Channels", icon: "ri-whatsapp-line" },
  { value: "templates", slug: "templates", label: "Templates", icon: "ri-file-text-line" },
  { value: "quick-replies", slug: "quick-replies", label: "Quick replies", icon: "ri-flashlight-line" },
  { value: "notifications", slug: "notifications", label: "Notifications", icon: "ri-notification-3-line" },
  { value: "consent", slug: "consent", label: "Consent", icon: "ri-shield-user-line" },
];

const WhatsAppSettingsPanel = () => {
  const [activeTab, setActiveTab] = useState("channels");
  const [channels, setChannels] = useState([]);
  const [loadingChannels, setLoadingChannels] = useState(true);

  // Channels are needed by most tabs (channel pickers), so they are loaded once here.
  const loadChannels = useCallback(async () => {
    setLoadingChannels(true);
    const res = await listChannels();
    setLoadingChannels(false);
    if (!ok(res)) {
      toast.error(errorText(res, "Could not load WhatsApp channels"));
      return;
    }
    setChannels(res.messagingchannel_list || []);
  }, []);

  useEffect(() => {
    loadChannels();
  }, [loadChannels]);

  return (
    <SettingsPanel
      title="WhatsApp"
      description="Your WhatsApp numbers, approved templates, quick replies, automatic notifications and opt-outs."
      tabs={TABS}
      value={activeTab}
      onChange={setActiveTab}
      className="wa-settings"
    >
      {activeTab === "channels" && <ChannelsTab channels={channels} loading={loadingChannels} reload={loadChannels} />}
      {activeTab === "templates" && <TemplatesTab channels={channels} />}
      {activeTab === "quick-replies" && <QuickRepliesTab channels={channels} />}
      {activeTab === "notifications" && <NotificationsTab channels={channels} />}
      {activeTab === "consent" && <ConsentTab />}
    </SettingsPanel>
  );
};

const WhatsAppSettings = () => (
  <ModuleGate
    code={WHATSAPP_MODULE_CODE}
    moduleName="WhatsApp"
    description="Turn on the WhatsApp module to connect your Twilio WhatsApp numbers and message customers from CRMit."
  >
    <WhatsAppSettingsPanel />
  </ModuleGate>
);

export default WhatsAppSettings;
